<template>
    <div>
        <b-datatable
            ref="gridDispensedPatients"
            :key="datatableKey"
            :columns="columns"
            :endpoint="listRoute"
            :hidden-columns="hiddenColumns"
            :initial-filters="initialFilters"
            has-filter-selector
            item-key="id"
        >
            <template #buttons>
                <div class="d-flex">
                    <div class="px-2">
                        <v-tooltip top transition="v-fab-transition">
                            <template #activator="{ on: onTooltip, attrs: attrsTooltip }">
                                <v-menu offset-y>
                                    <template #activator="{ on, attrs }">
                                        <v-btn
                                            color="primary"
                                            dark
                                            icon
                                            v-bind="{ ...attrs, ...attrsTooltip }"
                                            v-on="{ ...on, ...onTooltip }"
                                        >
                                            <v-icon>mdi-file-export-outline</v-icon>
                                        </v-btn>
                                    </template>
                                    <v-list dense>
                                        <v-list-item @click="exportRecord('csv')">
                                            <v-list-item-icon>
                                                <v-icon>mdi mdi-file-delimited</v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-title>{{
                                                t('patients::button.export_csv')
                                            }}</v-list-item-title>
                                        </v-list-item>
                                        <v-list-item @click="exportRecord('xls')">
                                            <v-list-item-icon>
                                                <v-icon>mdi mdi-file-excel</v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-title>{{
                                                t('patients::button.export_xls')
                                            }}</v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                            </template>
                            <span>{{ t('patients::button.export') }}</span>
                        </v-tooltip>
                    </div>
                </div>
            </template>
            <template #filters="{ filters }">
                <v-col cols="4">
                    <b-filter-input
                        v-model="filters.filters.dms_id"
                        :label="t('patients::grid.label.dms_id')"
                    ></b-filter-input>
                </v-col>
                <v-col cols="4">
                    <b-filter-select
                        v-model="filters.filters.is_completed"
                        :label="t('patients::grid.label.is_completed')"
                        :items="filtersOptions.is_completed"
                    ></b-filter-select>
                </v-col>
            </template>
            <template #[`item.validity_date`]="{ item }">
                {{ item.validity_date ? moment(item.validity_date).format($root.dateFormat) : t('default::label.na') }}
            </template>
            <template #[`item.last_blood_sample_color`]="{ item }">
                <div v-if="item.last_blood_sample_color" :class="`blood-status ${item.last_blood_sample_color}--text`">
                    <div :class="`status ${item.last_blood_sample_color}--text`">
                        <v-icon>icon icon-blood-result</v-icon>
                    </div>
                    <span v-text="item.last_blood_sample_color"></span>
                </div>
                <div v-else>
                    <span>{{ t('default::label.na') }}</span>
                </div>
            </template>
            <template #[`item.next_due_date`]="{ item }">
                {{ item.next_due_date ? moment(item.next_due_date).format($root.dateFormat) : t('default::label.na') }}
            </template>
            <template #[`item.blood_testing_frequency`]="{ item }">
                <span>
                    {{
                        item.blood_testing_frequency
                            ? t('patients::grid.label.' + item.blood_testing_frequency)
                            : t('default::label.na')
                    }}
                </span>
            </template>
            <template #[`item.actions`]="{ item }">
                <div class="table-actions">
                    <v-tooltip v-if="can('api.v1.patients.switch-completed')" top transition="v-fab-transition">
                        <template #activator="{ on, attrs }">
                            <v-btn color="disabled-color" icon v-bind="attrs" v-on="on" @click="switchCompleted(item)">
                                <v-icon v-if="!item.is_completed" class="active-pill">mdi-pill</v-icon>
                                <v-icon v-else>mdi-pill</v-icon>
                            </v-btn>
                        </template>
                        <span v-if="!item.is_completed">{{ t('patients::button.complete') }}</span>
                        <span v-else>{{ t('patients::button.not_completed') }}</span>
                    </v-tooltip>
                </div>
            </template>
        </b-datatable>
    </div>
</template>

<script>
export default {
    props: {
        listRoute: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            datatableKey: 0,
            downloadingFile: false,
            initialFilters: { is_completed: 0, is_registred: 1 },
            hiddenColumns: ['id', 'is_completed'],
            columns: [
                {
                    text: t('default::grid.label.id'),
                    value: 'id',
                    sortable: false,
                    width: 170,
                },
                {
                    text: t('patients::grid.label.completed'),
                    value: 'is_completed',
                    width: 140,
                    sortable: true,
                },
                {
                    text: t('patients::grid.label.dms_id'),
                    value: 'dms_id',
                    width: 140,
                    sortable: true,
                },
                {
                    text: t('patients::grid.label.validity'),
                    value: 'validity_date',
                    width: 140,
                    sortable: true,
                },
                {
                    text: t('patients::grid.label.blood_status'),
                    value: 'last_blood_sample_color',
                    width: 140,
                    sortable: true,
                },
                {
                    text: t('patients::grid.label.blood_testing_frequency'),
                    value: 'blood_testing_frequency',
                    width: 140,
                    sortable: true,
                },
                {
                    text: t('patients::grid.label.next_due_date'),
                    value: 'next_due_date',
                    width: 140,
                    sortable: true,
                },
                {
                    text: t('default::grid.label.actions'),
                    value: 'actions',
                    sortable: false,
                    fields: null,
                    width: 180,
                    align: 'end',
                },
            ],
            filtersOptions: {
                is_completed: [
                    {
                        text: t('default::options.all'),
                        value: 'all',
                    },
                    {
                        text: t('default::options.yes'),
                        value: 1,
                    },
                    {
                        text: t('default::options.no'),
                        value: 0,
                    },
                ],
            },
        };
    },
    methods: {
        switchCompleted(item) {
            axios
                .put(route('api.v1.patients.switch-completed', { patient: item.id }), {
                    is_completed: !item.is_completed,
                })
                .then((response) => {
                    this.datatableKey++;
                    this.$root.notify('info', response.data.message);
                })
                .catch((e) => console.log(e));
        },
        async exportRecord(format) {
            const filters = this.$refs.gridDispensedPatients.filters || {};
            const configHiddenColumns = this.$store.state.data_table.hiddenColumns;
            const visibleColumns = this.columns
                .filter((col) => !configHiddenColumns.includes(col.value))
                .map((col) => col.value)
                .filter(function (item) {
                    return item !== 'actions';
                });

            const link = document.createElement('a');
            link.href = route('patients.export', {
                format,
                ...filters,
                fields: visibleColumns.join(','),
            });
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
    },
};
</script>
<style scoped>
.active-pill {
    color: #3cacb6 !important;
}
</style>
